import { useRef } from "react";
import type { Product } from "@shared/types";
import { trpc } from "@/lib/trpc";
import { usePersistFn } from "./usePersistFn";

const ANALYTICS_SESSION_STORAGE_KEY = "techfare.analyticsSessionId";
const SEARCH_RESULT_WEIGHTS = [1, 0.7, 0.45, 0.3, 0.2] as const;

type ProductAnalyticsEvent = {
  productId: number;
  eventType: "view" | "click" | "search";
  source?: string;
  searchTerm?: string;
  weight?: number;
};

function getAnalyticsSessionId() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const existingSessionId = localStorage.getItem(
      ANALYTICS_SESSION_STORAGE_KEY
    );
    if (existingSessionId) {
      return existingSessionId;
    }

    const nextSessionId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    localStorage.setItem(ANALYTICS_SESSION_STORAGE_KEY, nextSessionId);
    return nextSessionId;
  } catch {
    return null;
  }
}

export function useProductAnalytics() {
  const trackMutation = trpc.analytics.track.useMutation();
  const sessionIdRef = useRef<string | null>(null);

  const trackEvents = usePersistFn((events: ProductAnalyticsEvent[]) => {
    if (events.length === 0) {
      return;
    }

    if (!sessionIdRef.current) {
      sessionIdRef.current = getAnalyticsSessionId();
    }

    trackMutation.mutate({
      events: events.map(event => ({
        productId: event.productId,
        eventType: event.eventType,
        source: event.source,
        searchTerm: event.searchTerm,
        sessionId: sessionIdRef.current ?? undefined,
        weight: event.weight,
      })),
    });
  });

  const trackProductView = usePersistFn(
    (productId: number, source?: string) => {
      trackEvents([{ productId, eventType: "view", source }]);
    }
  );

  const trackProductClick = usePersistFn(
    (productId: number, source?: string) => {
      trackEvents([{ productId, eventType: "click", source }]);
    }
  );

  const trackSearchResults = usePersistFn(
    (searchTerm: string, products: Product[], source?: string) => {
      const trimmedTerm = searchTerm.trim();
      if (!trimmedTerm) {
        return;
      }

      const seenProductIds = new Set<number>();
      const events: ProductAnalyticsEvent[] = [];

      for (let index = 0; index < products.length; index += 1) {
        const product = products[index];
        if (
          index >= SEARCH_RESULT_WEIGHTS.length ||
          seenProductIds.has(product.id)
        ) {
          continue;
        }

        seenProductIds.add(product.id);
        events.push({
          productId: product.id,
          eventType: "search",
          source,
          searchTerm: trimmedTerm,
          weight: SEARCH_RESULT_WEIGHTS[index],
        });
      }

      trackEvents(events);
    }
  );

  return {
    trackProductView,
    trackProductClick,
    trackSearchResults,
  };
}
